import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { useUser, type User } from './useData';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useModuleEntitlements } from '../module-management';
import { usePublishedEForms, useEFormAppPages, useEFormSidebarReports } from '../../e-forms/useData';
import { Button } from '../../../components/ui/Button';
import { ChevronLeftIcon } from '../../../components/ui/Icons';
import { cn } from '../../../lib/utils';
import SidebarCustomizer from '../../../components/Layout/SidebarCustomizer';
import {
  getSidebarItems,
  toPublishedEFormSidebarInput,
  withPublishedEFormsInSidebar,
  withEFormAppPagesInSidebar,
  withSavedEFormReportsInSidebar,
} from '../../../components/Layout/config/SidebarItems';
import {
  fetchSidebarLayout,
  saveSidebarLayout,
  type SidebarLayout,
} from '../../../utils/sidebarLayout';

interface PermissionRow {
  moduleKey: string;
  canView: boolean;
}

const UserMenuPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const { user: currentUser } = useAuth();
  const { entitlements } = useModuleEntitlements();
  const userId = id ? parseInt(id, 10) : NaN;
  const { user, isLoading: userLoading } = useUser(userId);
  const [layout, setLayout] = useState<SidebarLayout | null>(null);
  const [layoutLoading, setLayoutLoading] = useState(true);
  const [permissions, setPermissions] = useState<PermissionRow[] | null>(null);
  const [otherUsers, setOtherUsers] = useState<User[]>([]);
  const [copyFromUserId, setCopyFromUserId] = useState('');
  const [copying, setCopying] = useState(false);

  const canViewEFormSubmissions = user?.role === 'ADMIN' || permissions?.some((p) => p.moduleKey === 'e_form_submissions' && p.canView);
  const publishedEForms = usePublishedEForms(undefined, 1, 200, undefined, true);
  const sidebarReports = useEFormSidebarReports(Boolean(canViewEFormSubmissions || user?.role === 'ADMIN'));
  const appPages = useEFormAppPages(undefined, Boolean(canViewEFormSubmissions || user?.role === 'ADMIN'));

  useEffect(() => {
    if (!id || Number.isNaN(userId)) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLayoutLoading(true);
        const [layoutRes, permRes] = await Promise.all([
          fetchSidebarLayout(userId),
          user?.role === 'ADMIN'
            ? Promise.resolve(null)
            : api.get<PermissionRow[]>(`/users/${userId}/permissions`).then((r) => (Array.isArray(r.data) ? r.data : [])),
        ]);
        if (cancelled) return;
        setLayout(layoutRes);
        setPermissions(permRes);
      } catch (e) {
        console.error(e);
        showError('Failed to load menu layout');
        if (!cancelled) {
          setLayout(null);
          setPermissions([]);
        }
      } finally {
        if (!cancelled) setLayoutLoading(false);
      }
    };
    if (user) load();
    return () => {
      cancelled = true;
    };
  }, [userId, id, user, showError]);

  useEffect(() => {
    if (userLoading || !userId) return;
    const loadUsers = async () => {
      try {
        const res = await api.get<User[] | { data: User[] }>('/users');
        const list = Array.isArray(res.data) ? res.data : (res.data as { data: User[] })?.data ?? [];
        setOtherUsers(list.filter((u) => u.id !== userId));
      } catch {
        setOtherUsers([]);
      }
    };
    loadUsers();
  }, [userId, userLoading]);

  const canViewForUser = useMemo(() => {
    if (!user) return undefined;
    if (user.role === 'ADMIN') return undefined;
    const allowed = new Set((permissions ?? []).filter((p) => p.canView).map((p) => p.moduleKey));
    return (moduleKey: string) => allowed.has(moduleKey);
  }, [user, permissions]);

  const baseItems = useMemo(() => {
    const base = getSidebarItems(canViewForUser, user?.vesselId ?? null, entitlements);
    const forms = canViewEFormSubmissions || user?.role === 'ADMIN'
      ? toPublishedEFormSidebarInput(publishedEForms.data ?? [])
      : [];
    const reports = canViewEFormSubmissions || user?.role === 'ADMIN' ? sidebarReports.data ?? [] : [];
    const pages = canViewEFormSubmissions || user?.role === 'ADMIN' ? appPages.data ?? [] : [];
    return withEFormAppPagesInSidebar(
      withSavedEFormReportsInSidebar(withPublishedEFormsInSidebar(base, forms), reports),
      pages,
    );
  }, [canViewForUser, user?.vesselId, user?.role, entitlements, canViewEFormSubmissions, publishedEForms.data, sidebarReports.data, appPages.data]);

  const handleCopyFromUser = async () => {
    const sourceId = copyFromUserId ? parseInt(copyFromUserId, 10) : NaN;
    if (Number.isNaN(sourceId)) {
      showError('Select a user to copy from');
      return;
    }
    try {
      setCopying(true);
      const copied = await fetchSidebarLayout(sourceId);
      setLayout(copied);
      setCopyFromUserId('');
      showSuccess('Menu layout copied. Review and save to apply.');
    } catch (e) {
      console.error(e);
      showError('Failed to load menu from user');
    } finally {
      setCopying(false);
    }
  };

  const persistLayout = async (next: SidebarLayout | null) => {
    const saved = await saveSidebarLayout(next, {
      userId,
      applyLocally: currentUser?.id === userId,
    });
    setLayout(saved);
    return saved;
  };

  if (userLoading || !user) {
    return (
      <div className="p-6">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl flex flex-col h-[calc(100vh-6rem)] min-h-0">
      <div className="flex items-center gap-4 mb-4 flex-shrink-0">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-1 rounded hover:bg-black/10 dark:hover:bg-white/10"
          aria-label="Back"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold">Customize menu: {user.userDisplayName || user.userEmail}</h1>
          <p className="text-sm opacity-80">
            Reorder groups, add custom sections, or show modules without a group. E-form shortcuts are included.
          </p>
        </div>
        <div className={cn('flex flex-wrap items-center gap-2 flex-shrink-0', colors.textMuted)}>
          <span className="text-sm">Copy from:</span>
          <select
            value={copyFromUserId}
            onChange={(e) => setCopyFromUserId(e.target.value)}
            disabled={copying || otherUsers.length === 0}
            className={cn(
              'rounded border px-2 py-1.5 text-sm min-w-[180px]',
              colors.border,
              'bg-white dark:bg-gray-800',
              colors.text,
            )}
            aria-label="Select user to copy menu from"
          >
            <option value="">Select user…</option>
            {otherUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.userDisplayName || `${u.userFirstName || ''} ${u.userLastName || ''}`.trim() || u.userEmail}
              </option>
            ))}
          </select>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleCopyFromUser}
            disabled={!copyFromUserId || copying}
          >
            {copying ? 'Copying...' : 'Copy'}
          </Button>
        </div>
      </div>

      {layoutLoading ? (
        <p>Loading menu…</p>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pr-1 -mr-1">
          <SidebarCustomizer
            embedded
            baseItems={baseItems}
            initialLayout={layout}
            persistLayout={persistLayout}
          />
        </div>
      )}
    </div>
  );
};

export default UserMenuPage;
